import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

export const Route = createFileRoute('/_site/compare/hyperping')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'Hyperping Alternative — Uptrack Uptime Monitoring' },
      { name: 'description', content: "Uptrack vs Hyperping: 30-second checks start at $24/mo on Hyperping and $3/mo on Uptrack. Compare intervals, seats, status pages, and alerts." },
      { property: 'og:title', content: 'Hyperping Alternative — Uptrack' },
      { property: 'og:description', content: "Hyperping Essentials is $24/mo for 50 monitors at 30s. Uptrack Pro is $3/mo for 50 monitors at 30s. Compare features." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-hyperping.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/hyperping' },
    ],
    scripts: comparisonScripts('hyperping', "Hyperping"),
  }),
})

const data: ComparisonData = {
  competitor: "Hyperping",
  tagline: "Hyperping offers uptime monitoring, browser checks, on-call, and status pages, with 30-second checks from $24/mo. Uptrack checks every 30 seconds from $3/mo.",
  heroDescription: "These facts come from Hyperping's public pricing and docs pages as of October 2026. Plans change often, so check their site before you decide.",

  features: [
    { feature: "Free plan", uptrack: "5 monitors at 30 s from Nuremberg", competitor: "20 monitors at 5 min, 1 status page" },
    { feature: "30-second checks", uptrack: "Pro, $3/mo (50 monitors)", competitor: "Essentials, $24/mo (50 monitors)", uptrackWins: true },
    { feature: "Status pages (entry paid plan)", uptrack: "5 on Pro", competitor: "1 on Essentials", uptrackWins: true },
    { feature: "Fastest check interval", uptrack: "30 sec", competitor: "20 sec (Business, $249/mo)" },
    { feature: "Down confirmation", uptrack: "2-5 consecutive failed checks; several check locations must confirm", competitor: "Other selected regions re-check before an outage opens" },
    { feature: "Check regions", uptrack: MONITOR_LOCATIONS.compare.cell, competitor: "18", uptrackWins: MONITOR_LOCATIONS.compare.wins },
    { feature: "Browser checks", uptrack: "Playwright (5 on Pro)", competitor: "Playwright-based" },
    { feature: "On-call", uptrack: "Escalation policies (Team and Business)", competitor: "Built-in on-call scheduling" },
  ],

  painPoints: [
    {
      title: "Price of 30-second checks",
      description: "Hyperping's 30-second checks start on Essentials at $24/mo for 50 monitors. Uptrack Pro checks 50 monitors every 30 seconds for $3/mo.",
    },
    {
      title: "Status pages per plan",
      description: "Hyperping Essentials includes 1 status page, and Pro ($74/mo) includes 3. Uptrack Pro includes 5 status pages, and Team includes unlimited pages with custom domains.",
    },
    {
      title: "Seats",
      description: "Hyperping sells extra seats at $7-12/mo each, depending on the plan. Uptrack plans include a fixed number of seats: 3 on Pro, 5 on Team, 15 on Business.",
    },
  ],

  differentiators: [
    "30-second checks on every plan: Pro is $3/mo for 50 monitors, Team is $20/mo for 200, Business is $79/mo for 1,000.",
    "Status pages on every plan: 1 on free, 5 on Pro, unlimited on Team and Business, with email subscriber notifications on paid plans.",
    "Alert confirmation: Uptrack opens an incident only after 2-5 consecutive failed checks, and several check locations must confirm. One DNS timeout or CDN blip does not page you.",
    "Browser checks: Pro includes 5 Playwright monitors that run flows like login or checkout and save a screenshot at each step.",
    "REST API and an MCP server, so you can manage monitors from scripts or AI assistants such as Claude.",
  ],

  notForYou: [
    "If you need checks faster than 30 seconds, Hyperping's Business plan checks every 20 seconds.",
    "If you need built-in on-call scheduling with phone call alerts, Hyperping has a fuller incident management suite.",
    "If you need PagerDuty or Microsoft Teams integrations, Hyperping supports these natively.",
    "If you need to add seats one at a time, Hyperping sells extra seats on each plan.",
  ],
}
